<template>
  <HeaderComponent />
  
  <!-- Loading State with Page Structure -->
  <div v-if="loading">
    <div class="relative h-96 bg-gradient-to-br from-primary to-secondary overflow-hidden">
      <div class="absolute inset-0 bg-black/20"></div>
      <div class="relative z-10 container mx-auto px-4 h-full flex items-center justify-center">
        <div class="flex flex-col items-center justify-center">
          <div class="lds-ripple">
            <div></div>
            <div></div>
          </div>
          <p class="text-white mt-4 text-lg">Loading project details...</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Project Not Found -->
  <div v-else-if="!project" class="min-h-screen flex items-center justify-center">
    <div class="text-center">
      <h1 class="text-4xl font-bold text-gray-900 mb-4">Project Not Found</h1>
      <p class="text-gray-600 mb-8">The project you're looking for doesn't exist.</p>
      <router-link 
        to="/projects" 
        class="inline-block bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
      >
        Back to Projects
      </router-link>
    </div>
  </div>

  <!-- Project Details -->
  <div v-else :key="$route.params.slug">
    <!-- Hero Section -->
    <div class="relative h-96 bg-gradient-to-br from-primary to-secondary overflow-hidden">
      <div class="absolute inset-0 bg-black/20"></div>
      <div class="relative z-10 container mx-auto px-4 h-full flex items-center">
        <div class="max-w-4xl">
          <nav class="flex items-center space-x-2 text-white/80 mt-12 mb-4" data-sal="slide-up" data-sal-delay="200">
            <router-link to="/" class="hover:text-white transition-colors">Home</router-link>
            <span>/</span>
            <router-link to="/projects" class="hover:text-white transition-colors">Projects</router-link>
            <span>/</span>
            <span class="text-white">{{ project.name }}</span>
          </nav>
          <h1 class="text-4xl md:text-6xl font-bold font-heading text-white mb-4" data-sal="slide-up" data-sal-delay="400">
            {{ project.name }}
          </h1>
          <p class="text-xl text-white/90 mb-6" data-sal="slide-up" data-sal-delay="600">
            {{ project.description }}
          </p>
          <div class="flex flex-wrap items-center gap-4" data-sal="slide-up" data-sal-delay="800">
            <span class="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
              {{ project.category }}
            </span>
            <span class="text-white/80">
              <svg class="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"></path>
              </svg>
              {{ formatReleaseDate(project.releaseDate) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Project Content -->
    <div class="py-20">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <!-- Main Content -->
          <div class="lg:col-span-2">
            <!-- Project Image -->
            <div class="relative mb-12" data-sal="slide-up" data-sal-delay="200">
              <div class="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  :src="project.image" 
                  :alt="project.alt"
                  class="w-full h-full object-cover"
                >
              </div>
            </div>

            <!-- Project Description -->
            <div class="prose prose-lg max-w-none mb-12" data-sal="slide-up" data-sal-delay="400">
              <h2 class="text-3xl font-bold font-heading text-gray-900 mb-6">About This Project</h2>
              <div class="text-gray-600 leading-relaxed mb-6 prose prose-lg max-w-none">
                <div v-html="project.description || 'No detailed description available.'"></div>
              </div>
              
              <h3 class="text-2xl font-bold font-heading text-gray-900 mb-4">Challenge</h3>
              <p class="text-gray-600 leading-relaxed mb-6">
                {{ project.challenge || 'The main challenge was to create a modern, user-friendly solution that meets all client requirements while maintaining excellent performance and accessibility standards.' }}
              </p>

              <h3 class="text-2xl font-bold font-heading text-gray-900 mb-4">Solution</h3>
              <p class="text-gray-600 leading-relaxed mb-6">
                {{ project.solution || 'We developed a comprehensive solution using modern technologies and best practices, ensuring optimal user experience across all devices and platforms.' }}
              </p>

              <h3 class="text-2xl font-bold font-heading text-gray-900 mb-4">Results</h3>
              <p class="text-gray-600 leading-relaxed">
                {{ project.results || 'The project was delivered successfully, exceeding client expectations and achieving all targeted goals for performance, usability, and business impact.' }}
              </p>
            </div>

            <!-- Technologies Used -->
            <div class="mb-12" data-sal="slide-up" data-sal-delay="600">
              <h3 class="text-2xl font-bold font-heading text-gray-900 mb-6">Technologies Used</h3>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div 
                  v-for="tech in project.technologies" 
                  :key="tech"
                  class="bg-white border border-[#d1d5db] rounded-lg p-4 text-center hover:shadow-lg transition-shadow duration-300"
                >
                  <div class="font-medium text-gray-900">{{ tech }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="lg:col-span-1">
            <div class="sticky top-8 space-y-8">
              <!-- Project Info -->
              <div class="bg-white rounded-2xl shadow-lg p-6" data-sal="slide-up" data-sal-delay="400">
                <h3 class="text-xl font-bold font-heading text-gray-900 mb-6">Project Info</h3>
                <div class="space-y-4">
                  <div>
                    <label class="text-sm font-medium text-gray-500 uppercase tracking-wider">Client</label>
                    <p class="text-gray-900 font-medium">{{ project.client || project.name }}</p>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-500 uppercase tracking-wider">Category</label>
                    <p class="text-gray-900 font-medium">{{ project.category }}</p>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-500 uppercase tracking-wider">Release Date</label>
                    <p class="text-gray-900 font-medium">{{ formatReleaseDate(project.releaseDate) }}</p>
                  </div>
                  <div v-if="project.url">
                    <label class="text-sm font-medium text-gray-500 uppercase tracking-wider">Live Site</label>
                    <p><a 
                      :href="project.url" 
                      target="_blank"
                      class="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors duration-300"
                    >
                      Visit Website
                      <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                      </svg>
                    </a></p>
                  </div>
                </div>
              </div>

              <!-- Call to Action -->
              <div class="bg-gradient-to-br from-primary to-secondary rounded-2xl p-6 text-white" data-sal="slide-up" data-sal-delay="600">
                <h3 class="text-xl font-bold font-heading mb-4">Like This Project?</h3>
                <p class="text-white/90 mb-6">Ready to bring your vision to life with a similar approach?</p>
                <router-link 
                  to="/contact"
                  class="inline-block bg-white text-primary hover:bg-gray-100 px-6 py-3 rounded-lg font-medium transition-colors duration-300 w-full text-center"
                >
                  Start Your Project
                </router-link>
              </div>

              <!-- Related Projects -->
              <div class="bg-white rounded-2xl shadow-lg p-6" data-sal="slide-up" data-sal-delay="800">
                <h3 class="text-xl font-bold font-heading text-gray-900 mb-6">Related Projects</h3>
                <div class="space-y-4">
                  <router-link 
                    v-for="relatedProject in relatedProjects" 
                    :key="relatedProject.id"
                    :to="`/projects/${relatedProject.slug}`"
                    class="block group"
                  >
                    <div class="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                      <div class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          :src="relatedProject.image" 
                          :alt="relatedProject.alt"
                          class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        >
                      </div>
                      <div class="flex-1 min-w-0">
                        <h4 class="font-medium text-gray-900 group-hover:text-primary transition-colors duration-300 truncate">
                          {{ relatedProject.name }}
                        </h4>
                        <p class="text-sm text-gray-500">{{ relatedProject.category }}</p>
                      </div>
                    </div>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="border-t border-[#d1d5db] py-8">
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <router-link 
            to="/projects"
            class="inline-flex items-center text-gray-600 hover:text-primary transition-colors duration-300"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            Back to Projects
          </router-link>
          
          <div class="flex items-center space-x-4">
            <router-link 
              v-if="previousProject"
              :to="`/projects/${previousProject.slug}`"
              class="inline-flex items-center text-gray-600 hover:text-primary transition-colors duration-300"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
              Previous
            </router-link>
            
            <router-link 
              v-if="nextProject"
              :to="`/projects/${nextProject.slug}`"
              class="inline-flex items-center text-gray-600 hover:text-primary transition-colors duration-300"
            >
              Next
              <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>

  <FooterComponent />
</template>
<script>
import HeaderComponent from '@/components/Header.vue'
import FooterComponent from '@/components/Footer.vue'
import LoadingRipple from '@/components/LoadingRipple.vue'
import { updateMetaTags } from '@/utils/seo'

export default {
  name: 'ProjectView',
  components: {
    HeaderComponent,
    FooterComponent,
    LoadingRipple
  },
  data() {
    return {
      loading: true,
      project: null,
      allProjects: []
    };
  },
  computed: {
    relatedProjects() {
      if (!this.project) return [];
      return this.allProjects
        .filter(p => {
          // Use slug as the primary comparison since it's more reliable
          const isDifferentProject = p.slug !== this.project.slug;
          const isSameCategory = p.category === this.project.category;
          return isDifferentProject && isSameCategory;
        })
        .slice(0, 3);
    },
    currentProjectIndex() {
      if (!this.project) return -1;
      return this.allProjects.findIndex(p => p.slug === this.project.slug);
    },
    previousProject() {
      const index = this.currentProjectIndex;
      return index > 0 ? this.allProjects[index - 1] : null;
    },
    nextProject() {
      const index = this.currentProjectIndex;
      return index < this.allProjects.length - 1 ? this.allProjects[index + 1] : null;
    }
  },
  async created() {
    await this.loadAllProjects();
    this.loadProject();
  },
  watch: {
    '$route.params.slug'(newSlug, oldSlug) {
      if (newSlug && newSlug !== oldSlug) {
        this.loadProject();
      }
    }
  },
  methods: {
    updateProjectSEO() {
      if (this.project) {
        // Create truncated description for meta tags
        let metaDescription = this.project.description || '';
        if (typeof metaDescription === 'string' && metaDescription.length > 155) {
          metaDescription = metaDescription.substring(0, 152) + '...';
        }
        
        updateMetaTags({
          title: `${this.project.name} - Project Case Study | Diginova`,
          description: `${metaDescription} See how Diginova created this ${this.project.category?.toLowerCase() || 'digital'} solution with expert design and development.`,
          keywords: `${this.project.name}, ${this.project.category}, ${this.project.technologies?.join(', ') || ''}, project case study, web design, development`,
          canonical: `https://diginova.ch/projects/${this.project.slug}`,
          ogImage: this.project.image || 'https://diginova.ch/assets/img/pexels-fauxels-3184433.jpg',
          ogType: 'article'
        });
      }
    },
    formatReleaseDate(dateStr) {
      if (!dateStr) return 'N/A';
      
      // Convert YYYY-MM to a Date object and format it
      const [year, month] = dateStr.split('-');
      const date = new Date(year, parseInt(month) - 1);
      
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      });
    },
    async loadAllProjects() {
      // Load lightweight projects index for navigation
      try {
        // Try to load from NocoDB first
        try {
          const nocoService = (await import('@/services/nocodb.js')).default;
          const data = await nocoService.getProjects();
          
          if (data && data.list && data.list.length > 0) {
            this.allProjects = data.list.map(project => ({
              ...project,
              image: project.image && project.image.length > 0
                ? `https://web-db.drods.ch/${project.image[0].signedPath || project.image[0].path}`
                : 'https://images.unsplash.com/photo-1754039985001-ccafee437736?w=150&h=150&fit=crop&crop=face&auto=format',
              technologies: typeof project.technologies === 'string' 
                ? project.technologies.split(', ').map(tech => tech.trim())
                : project.technologies || []
            }));
            return;
          }
        } catch (nocoError) {
          // Silently handle NocoDB connection issues
        }
        
        // Fallback to local JSON file
        const indexModule = await import('@/assets/projects/index.json');
        this.allProjects = indexModule.default.projects;
      } catch (error) {
        // Set empty array as fallback
        this.allProjects = [];
      }
    },
    async loadProject() {
      this.loading = true;
      this.project = null; // Clear current project while loading
      
      try {
        const slug = this.$route.params.slug;
        
        // Try to load from NocoDB first
        try {
          const nocoService = (await import('@/services/nocodb.js')).default;
          const projectData = await nocoService.getProjectBySlug(slug);
          
          if (projectData) {
            this.project = {
              ...projectData,
              image: projectData.image && projectData.image.length > 0
                ? `https://web-db.drods.ch/${projectData.image[0].signedPath || projectData.image[0].path}`
                : 'https://images.unsplash.com/photo-1754039985001-ccafee437736?w=150&h=150&fit=crop&crop=face&auto=format',
              technologies: typeof projectData.technologies === 'string' 
                ? projectData.technologies.split(', ').map(tech => tech.trim())
                : projectData.technologies || []
            };
            this.updateProjectSEO();
            return;
          }
        } catch (nocoError) {
          // Silently handle NocoDB connection issues
        }
        
        // Fallback to local JSON file
        try {
          const module = await import(`@/assets/projects/${slug}.json`);
          this.project = module.default;
          this.updateProjectSEO();
        } catch (importError) {
          // Project not found
          this.project = null;
        }
        
        // Reinitialize scroll animations after content loads
        this.$nextTick(() => {
          // Reinitialize scroll animations
          setTimeout(() => {
            if (typeof sal !== 'undefined') {
              sal({
                duration: 500,
                threshold: 0.1,
                once: true
              });
            }
          }, 150);
        });
        
      } catch (error) {
        // Redirect to 404 if project JSON file doesn't exist
        this.$router.push('/404');
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    // Initialize scroll animations
    setTimeout(() => {
      if (typeof sal !== 'undefined') {
        sal({
          duration: 500,
          threshold: 0.1,
          once: true
        });
      }
    }, 100);
  }
}

</script>

<style scoped>
.prose h2, .prose h3 {
  color: inherit;
}

/* Loading ripple animation */
.lds-ripple,
.lds-ripple div {
  box-sizing: border-box;
}

.lds-ripple {
  display: inline-block;
  position: relative;
  width: 120px;
  height: 120px;
}

.lds-ripple div {
  position: absolute;
  border: 6px solid var(--color-accent);
  opacity: 1;
  border-radius: 50%;
  animation: lds-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
}

.lds-ripple div:nth-child(2) {
  animation-delay: -0.5s;
  border-color: rgba(255, 255, 255, 0.8);
}

@keyframes lds-ripple {
  0% {
    top: 54px;
    left: 54px;
    width: 12px;
    height: 12px;
    opacity: 0;
  }
  4.9% {
    top: 54px;
    left: 54px;
    width: 12px;
    height: 12px;
    opacity: 0;
  }
  5% {
    top: 54px;
    left: 54px;
    width: 12px;
    height: 12px;
    opacity: 1;
  }
  100% {
    top: 0;
    left: 0;
    width: 120px;
    height: 120px;
    opacity: 0;
  }
}
</style>
